import * as _ from 'lodash-es';
import { AuthenticationService } from 'app/core/authentication';
import { buildLocaleUrlForLanguage } from 'app/core/internationalization/locale-url-builder';
import { DataTrackLabelEnum } from 'app/shared/enums/data-track-label.enum';
import { EventEmitter, Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { GuardsCheckEnd, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Route, Router } from '@angular/router';
import { KhAnalyticsService } from 'app/shared/services/kh-analytics.service';
import { Logger } from 'app/core/logger/services/logger.service';
import { MatomoTracker } from 'ngx-matomo-client';
import { TranslationService } from 'app/core/internationalization/translation.service';
import { ExternalLinkGuard } from 'app/core/navigation/guards/external-link.guard';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AppNavigationService {

  private isNavigating = false;
  private targetRouteUrl: string | null = null;
  private routeActive = false;
  private lastInitiatedNavigation = '';
  public pageViewed: EventEmitter<string> = new EventEmitter();

  public constructor(
    private readonly router: Router,
    private readonly logger: Logger,
    private readonly khAnalyticsService: KhAnalyticsService,
    private readonly tracker: MatomoTracker,
    private readonly authService: AuthenticationService,
    private readonly translationService: TranslationService)
  {
    this.handleRouterEvents();
  }

  public hasRoute(): boolean {
    return this.isNavigating;
  }

  private handleRouterEvents(): void {
    this.router.events.pipe(filter((event): event is NavigationStart => event instanceof NavigationStart))
      .pipe(untilDestroyed(this))
      .subscribe(this.initNavigation.bind(this));
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .pipe(untilDestroyed(this))
      .subscribe((event: NavigationEnd) => {
        this.cleanNavigation(event.url);
        this.emitPageViewedEvent(event.url);
        this.matomoTracking(event);
        const lang = this.translationService.getCurrentLanguage();
        if (this.authService.getLanguageChanged) {
          this.authService.resetLanguageChanged();
          const newUrl = buildLocaleUrlForLanguage(event.urlAfterRedirects, lang);
          if (newUrl !== window.location.href) {
            window.location.href = newUrl;
          }
        }

      });
    this.router.events.pipe(filter((event): event is GuardsCheckEnd => event instanceof GuardsCheckEnd))
      .pipe(untilDestroyed(this))
      .subscribe(this.setRouteActivation.bind(this));
    this.router.events.pipe(filter((event): event is NavigationCancel => event instanceof NavigationCancel))
      .pipe(untilDestroyed(this))
      .subscribe(this.cancelNavigation.bind(this));
    this.router.events.pipe(filter((event): event is NavigationError => event instanceof NavigationError))
      .pipe(untilDestroyed(this))
      .subscribe(error => {
        this.logger.error(`Navigation to ${error.url} failed`, error);
      });
  }

  private initNavigation(event: NavigationStart): void {
    this.lastInitiatedNavigation = event.url;
    if (!this.isNavigating) {
      this.isNavigating = true;
      this.targetRouteUrl = event.url;
      this.routeActive = false;
    }
  }

  private setRouteActivation(event: GuardsCheckEnd): void {
    if (this.targetRouteUrl === event.url) {
      this.routeActive = event.shouldActivate;
    }
  }

  private cancelNavigation(event: NavigationCancel): void {
    if (!this.routeActive) {
      this.cleanNavigation(event.url);
    }
  }

  private cleanNavigation(url: string): void {
    if (this.targetRouteUrl === url) {
      this.isNavigating = false;
      this.targetRouteUrl = '';
      this.routeActive = false;
    }
  }

  private navigateToCurrentRoute(url: string): void {
    if (this.targetRouteUrl === url && this.routeActive) {
      this.router.navigateByUrl(this.targetRouteUrl);
    }
  }

  private IsGuarded(route: Route): boolean {
    return !!route.canActivate && route.canActivate.some(guard => guard === ExternalLinkGuard);
  }

  public IsExternalGuarded(url: string): boolean {
    const urlsRoute = _.find(this.router.config, route => route.path === url.substr(1));
    if (!!urlsRoute &&
        (this.IsGuarded(urlsRoute) || (!!urlsRoute.children && _.some(urlsRoute.children, c => !c.path && this.IsGuarded(c))))) {

      return true;
    }

    return false;
  }

  private matomoTracking(event: NavigationEnd) {
    this.tracker.setCustomUrl(event.url);
    this.tracker.trackPageView();
  }

  private emitPageViewedEvent(url: string): void {
    this.khAnalyticsService.trackEvent({
      eventType: DataTrackLabelEnum.PageViewed,
      jsonData: {
        url
      }
    });
  }

  public getLastNavigation(): string {
    return this.lastInitiatedNavigation;
  }
}
